import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { Form, Select } from 'formik-antd';
import { Col, Row } from 'antd';
import { useDispatch } from 'react-redux';
import { FormInput, CustomInput } from 'atoms';
import { searchPincodeV2 } from 'store/clientSliceV2';
import {
	FormContainer,
	DisabledInput,
	NextButton,
	SaveButton,
	CancelButton,
} from './clientFormStyles';
import {
	BasicInfoSchema,
	LEGAL_STRUCTURE_OPTIONS,
	PRIMARY_COST_CENTER_OPTIONS,
} from './clientFormConstants';

export default function ClientBasicInfoFormV2({
	basicInfoData,
	setBasicInfoData,
	setCurrentStep,
	currentStep,
	isEditMode,
	isViewMode,
	onCancel,
}) {
	const dispatch = useDispatch();

	// Basic Info Initial Values
	const initialValues = useMemo(() => {
		// Helper: Extract value from head_office_address object or fallback to direct value
		const extractAddressField = (fieldName, fallbackValue = '') => {
			const isObject =
				typeof basicInfoData?.head_office_address === 'object' &&
				basicInfoData?.head_office_address !== null;
			return isObject
				? basicInfoData.head_office_address[fieldName] || fallbackValue
				: basicInfoData?.[fieldName] || fallbackValue;
		};

		return {
			client_legal_name: basicInfoData?.client_legal_name || '',
			company_name: basicInfoData?.company_name || '',
			legal_structure: basicInfoData?.legal_structure || undefined,
			primary_cost_center: basicInfoData?.primary_cost_center || undefined,
			primary_poc_name: basicInfoData?.primary_poc_name || '',
			primary_poc_contact: basicInfoData?.primary_poc_contact || '',
			finance_poc_name: basicInfoData?.finance_poc_name || '',
			finance_poc_contact: basicInfoData?.finance_poc_contact || '',
			email_id: basicInfoData?.email_id || '',
			head_office_address: extractAddressField('address_line1'),
			pincode: extractAddressField('pincode'),
			zone: extractAddressField('zone'),
			city: extractAddressField('city'),
			state: extractAddressField('state'),
		};
	}, [basicInfoData]);

	// Handle pincode lookup - Auto-fetch zone, city, state when user types 6-digit pincode
	const handlePincodeLookup = (pincode, setFieldValue) => {
		if (pincode?.length === 6) {
			dispatch(searchPincodeV2({ pincode })).then((res) => {
				if (res) {
					setFieldValue('zone', res.zone || '');
					setFieldValue('city', res.city || '');
					setFieldValue('state', res.state || '');
				} else {
					// Clear fields if pincode not found
					setFieldValue('zone', '');
					setFieldValue('city', '');
					setFieldValue('state', '');
				}
			});
		} else if (pincode?.length < 6) {
			// Clear fields if user is still typing or deleted characters
			setFieldValue('zone', '');
			setFieldValue('city', '');
			setFieldValue('state', '');
		}
	};

	// Handle form submit
	const handleSubmit = (values) => {
		setBasicInfoData(values);
		setCurrentStep(1);
	};

	return (
		<FormContainer>
			<Formik
				initialValues={initialValues}
				validationSchema={BasicInfoSchema}
				onSubmit={handleSubmit}
				enableReinitialize
			>
				{({ setFieldValue }) => (
					<Form>
						<Row gutter={24}>
							<Col span={12}>
								<FormInput label="Client Legal Name*" name="client_legal_name">
									<CustomInput
										name="client_legal_name"
										placeholder="Eg : Intelligent Retail...."
										disabled={isViewMode}
									/>
								</FormInput>
							</Col>
							<Col span={12}>
								<FormInput label="Company Name*" name="company_name">
									<CustomInput
										name="company_name"
										placeholder="Eg : Ripplr"
										disabled={isViewMode}
									/>
								</FormInput>
							</Col>
						</Row>

						<Row gutter={24}>
							<Col span={12}>
								<FormInput label="Legal Structure*" name="legal_structure">
									<Select
										name="legal_structure"
										placeholder="Select"
										options={LEGAL_STRUCTURE_OPTIONS}
										style={{ width: '100%' }}
										disabled={isViewMode}
									/>
								</FormInput>
							</Col>
							<Col span={12}>
								<FormInput label="Primary Cost Center*" name="primary_cost_center">
									<Select
										name="primary_cost_center"
										placeholder="Select"
										options={PRIMARY_COST_CENTER_OPTIONS}
										style={{ width: '100%' }}
										disabled={isViewMode}
									/>
								</FormInput>
							</Col>
						</Row>

						<Row gutter={24}>
							<Col span={12}>
								<FormInput label="Primary PoC Name*" name="primary_poc_name">
									<CustomInput
										name="primary_poc_name"
										placeholder="Eg : Intelligent Retail...."
										disabled={isViewMode}
									/>
								</FormInput>
							</Col>
							<Col span={12}>
								<FormInput label="Primary PoC Contact*" name="primary_poc_contact">
									<CustomInput
										name="primary_poc_contact"
										placeholder="Eg : 9876543210"
										disabled={isViewMode}
										maxLength={10}
										onKeyPress={(e) => {
											// Only allow numbers
											if (!/[0-9]/.test(e.key)) {
												e.preventDefault();
											}
										}}
									/>
								</FormInput>
							</Col>
						</Row>

						<Row gutter={24}>
							<Col span={12}>
								<FormInput label="Finance PoC Name" name="finance_poc_name">
									<CustomInput
										name="finance_poc_name"
										placeholder="Eg : Intelligent Retail...."
										disabled={isViewMode}
									/>
								</FormInput>
							</Col>
							<Col span={12}>
								<FormInput label="Finance PoC Contact" name="finance_poc_contact">
									<CustomInput
										name="finance_poc_contact"
										placeholder="Eg : 9876543210"
										disabled={isViewMode}
										maxLength={10}
										onKeyPress={(e) => {
											// Only allow numbers
											if (!/[0-9]/.test(e.key)) {
												e.preventDefault();
											}
										}}
									/>
								</FormInput>
							</Col>
						</Row>

						<Row gutter={24}>
							<Col span={12}>
								<FormInput label="Email ID*" name="email_id">
									<CustomInput
										name="email_id"
										placeholder="Eg : example@gmail.com"
										disabled={isViewMode}
									/>
								</FormInput>
							</Col>
							<Col span={12}>
								<FormInput label="Head Office Address*" name="head_office_address">
									<CustomInput
										name="head_office_address"
										placeholder="Eg : Muneeshware layout, Bangalore"
										disabled={isViewMode}
									/>
								</FormInput>
							</Col>
						</Row>

						<Row gutter={24}>
							<Col span={6}>
								<FormInput label="Pincode*" name="pincode">
									<CustomInput
										name="pincode"
										placeholder="Eg : 517408"
										disabled={isViewMode}
										onChange={(e) => {
											setFieldValue('pincode', e.target.value);
											handlePincodeLookup(e.target.value, setFieldValue);
										}}
									/>
								</FormInput>
							</Col>
							<Col span={6}>
								<FormInput label="Zone*" name="zone">
									<DisabledInput name="zone" placeholder="South" disabled />
								</FormInput>
							</Col>
							<Col span={6}>
								<FormInput label="City" name="city">
									<DisabledInput name="city" placeholder="South" disabled />
								</FormInput>
							</Col>
							<Col span={6}>
								<FormInput label="State" name="state">
									<DisabledInput name="state" placeholder="South" disabled />
								</FormInput>
							</Col>
						</Row>

						{/* Navigation Buttons */}
						<Row justify="end" style={{ marginTop: 24, gap: 16 }}>
							{isEditMode && !isViewMode && (
								<CancelButton htmlType="button" onClick={onCancel}>
									Cancel
								</CancelButton>
							)}
							{(() => {
								if (isViewMode) {
									return (
										<NextButton type="button" onClick={() => setCurrentStep(1)}>
											Next
										</NextButton>
									);
								}
								if (!isEditMode && currentStep === 0) {
									return <NextButton type="submit">Next</NextButton>;
								}
								return <SaveButton type="submit">Save Changes</SaveButton>;
							})()}
						</Row>
					</Form>
				)}
			</Formik>
		</FormContainer>
	);
}

ClientBasicInfoFormV2.propTypes = {
	basicInfoData: PropTypes.object,
	setBasicInfoData: PropTypes.func.isRequired,
	setCurrentStep: PropTypes.func.isRequired,
	currentStep: PropTypes.number,
	isEditMode: PropTypes.bool,
	isViewMode: PropTypes.bool,
	onCancel: PropTypes.func.isRequired,
};

ClientBasicInfoFormV2.defaultProps = {
	basicInfoData: {},
	currentStep: 0,
	isEditMode: false,
	isViewMode: false,
};
